import { env } from './env'
import { getQueryClient } from './query-client'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let refreshPromise: Promise<string | null> | null = null

/**
 * Silently renew the access token using the stored refresh token.
 * Concurrent 401s share a single in-flight refresh call. Returns the new access
 * token, or null if refresh is impossible (no/expired/invalid refresh token).
 */
async function tryRefreshToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return null

      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      if (!res.ok) return null

      const data = (await res.json()) as { token?: string; refreshToken?: string }
      if (!data.token) return null

      localStorage.setItem('token', data.token)
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
      // Slide the Next.js middleware session cookie (30d)
      document.cookie = `auth_session=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
      return data.token
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
  _isRetry = false,
): Promise<T> {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  }

  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const activeCityId =
    typeof window !== 'undefined' ? localStorage.getItem('activeCityId') : null

  if (activeCityId) {
    ;(headers as Record<string, string>)['X-City-Id'] = activeCityId
  }

  const response = await fetch(`${env.NEXT_PUBLIC_API_URL}${path}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    if (response.status === 401) {
      // Only redirect to login if we're on an authenticated page (not already on auth pages)
      const isAuthPage = typeof window !== 'undefined' && (
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/register') ||
        window.location.pathname.startsWith('/forgot-password') ||
        window.location.pathname.startsWith('/reset-password') ||
        window.location.pathname.startsWith('/accept-invite')
      )

      // Silent refresh: before giving up, try renewing the access token with the
      // stored refresh token and replay the request once. Skipped on auth pages
      // (where a 401 means bad credentials, not an expired session) and on
      // retries (to avoid loops).
      if (!_isRetry && !isAuthPage && typeof window !== 'undefined') {
        const newToken = await tryRefreshToken()
        if (newToken) {
          return apiFetch<T>(path, options, true)
        }
      }

      if (!isAuthPage && typeof window !== 'undefined') {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }

      const body = await response.json().catch(() => ({}))
      throw new ApiError(
        body.error ?? 'Credenciais invalidas',
        401,
        body.code ?? 'UNAUTHORIZED',
      )
    }

    if (response.status === 402) {
      // Invalidate subscription cache so SubscriptionGuard re-fetches fresh status
      if (typeof window !== 'undefined') {
        try {
          getQueryClient().invalidateQueries({ queryKey: ['billing', 'subscription'] })
        } catch {
          // Query client not available yet — SubscriptionGuard will handle it
        }
      }
      const body = await response.json().catch(() => ({}))
      throw new ApiError(
        body.error ?? 'Assinatura inativa',
        402,
        body.code ?? 'PAYMENT_REQUIRED',
      )
    }

    const body = await response.json().catch(() => ({}))
    throw new ApiError(
      body.error ?? 'Erro inesperado',
      response.status,
      body.code,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('text/csv') || contentType.includes('text/plain')) {
    return response.text() as Promise<T>
  }

  return response.json() as Promise<T>
}
